import { useState, useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import { PlaybookHeader } from "@/components/customer-success/playbooks/PlaybookHeader"
import { PlaybookProgress } from "@/components/customer-success/playbooks/PlaybookProgress"
import { PlaybookViewToggle } from "@/components/customer-success/playbooks/PlaybookViewToggle"
import { PlaybookTaskGroup } from "@/components/customer-success/playbooks/PlaybookTaskGroup"
import { SubscriptionDetails } from "@/components/customer-success/playbooks/SubscriptionDetails"
import { Button } from "@/components/ui/button"
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { useToast } from "@/components/ui/use-toast"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { 
  Share2, 
  Copy, 
  ExternalLink, 
  Calendar, 
  Clock, 
  CheckCircle, 
  ArrowLeft, 
  Users, 
  AlertCircle, 
  Home, 
  CheckSquare, 
  Mail, 
  Send, 
  FileText, 
  Eye, 
  Play,
  Search,
  Check,
  Circle,
  X,
  MessageSquare,
  Building
} from "lucide-react"
import { PlaybookRun, TaskGroup, PlaybookTask } from "@/types/customer-success"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { formatDate } from "@/lib/utils"
import { supabase } from "@/lib/supabase"
import "@/styles/badge.css"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

export default function PlaybookRunDetail() {
  const { id } = useParams<{ id: string }>()
  const [playbookRun, setPlaybookRun] = useState<any>(null)
  const [taskGroups, setTaskGroups] = useState<any[]>([])
  const [view, setView] = useState<'internal' | 'participant'>('participant')
  const [showShareDialog, setShowShareDialog] = useState(false)
  const [publicLink, setPublicLink] = useState("")
  const [selectedTask, setSelectedTask] = useState<any>(null)
  const [showTaskDetailDialog, setShowTaskDetailDialog] = useState(false)
  const [showEmailTemplateDialog, setShowEmailTemplateDialog] = useState(false)
  const [showEditTaskDialog, setShowEditTaskDialog] = useState(false)
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [subscription, setSubscription] = useState<any>(null)
  const { toast } = useToast()
  const [users, setUsers] = useState<Record<string, any>>({})
  const [avatarUrlCache, setAvatarUrlCache] = useState<Record<string, string>>({})
  
  const fetchAvatarImage = async (filePath: string) => {
    try {
      // Check if we already have this avatar URL cached
      if (avatarUrlCache[filePath]) {
        return avatarUrlCache[filePath];
      }

      // If filePath is empty or undefined, return null immediately
      if (!filePath) {
        console.log('Avatar filename is empty or undefined');
        return null;
      }

      console.log('Fetching avatar with path:', filePath);
      
      // Extract just the filename if it contains a path
      const extractedFileName = filePath.split('/').pop();
      
      // Try to download the image from the storage
      const { data, error } = await supabase
        .storage
        .from('avatars')
        .download(`users/${extractedFileName}`);
        
      if (error) {
        console.error('Error downloading avatar:', error);
        return null;
      }
      
      // Create URL from blob for image display
      const url = URL.createObjectURL(data);
      
      // Cache the URL for future use
      setAvatarUrlCache(prevCache => ({
        ...prevCache,
        [filePath]: url
      }));
      
      console.log('Avatar loaded successfully:', filePath);
      return url;
    } catch (error) {
      console.error('Error processing avatar:', error);
      return null;
    }
  };

  useEffect(() => {
    const loadPlaybookRun = async () => {
      if (!id) {
        console.error("No ID provided");
        setIsLoading(false);
    toast({
          title: "Erro ao carregar playbook",
          description: "ID do playbook não fornecido.",
          variant: "destructive"
        });
        return;
      }

      console.log("Loading playbook run with ID:", id);
      try {
        setIsLoading(true);
        
        // 1. Fetch playbook run data
        console.log("Fetching playbook run data...");
        try {
          // First check if the table exists and what columns it has
          const { data: sampleRun, error: sampleRunError } = await supabase
            .from('playbook_runs')
            .select('*')
            .limit(1);
          
          if (sampleRunError) {
            console.error("Error checking playbook_runs table:", sampleRunError);
            if (sampleRunError.message?.includes("relation") && sampleRunError.message?.includes("does not exist")) {
              throw new Error("The playbook_runs table doesn't exist in the database");
            }
          } else if (sampleRun && sampleRun.length > 0) {
            console.log("Available columns in playbook_runs:", Object.keys(sampleRun[0]));
          }
          
          // Now fetch the specific run data
          const { data: runData, error: runError } = await supabase
            .from('playbook_runs')
            .select('*, template:playbook_template_id(*)')
            .eq('id', id)
            .single();

          if (runError) {
            console.error('Error loading playbook run:', runError);
            if (runError.code === 'PGRST116') {
              throw new Error('Playbook run not found with the provided ID');
    } else {
              throw runError;
            }
          }

          if (!runData) {
            console.error('Playbook run not found');
            throw new Error('Playbook run not found');
          }

          console.log("Loaded run data:", runData);
          
          // 2. Fetch subscription data if available
          let subscriptionData = null;
          if (runData.subscription_id) {
            console.log("Fetching subscription data for ID:", runData.subscription_id);
            try {
              // First try a simplified query to see if the subscription exists
              const { data: basicSubData, error: basicSubError } = await supabase
                .from('subscriptions')
                .select('id, state')
                .eq('id', runData.subscription_id)
                .single();
              
              if (basicSubError) {
                console.error("Error with basic subscription query:", basicSubError);
              } else if (basicSubData) {
                console.log("Basic subscription data found with state:", basicSubData.state);
                
                // Now try the full query
                const { data: subData, error: subError } = await supabase
                  .from('subscriptions')
                  .select(`
                    *,
                    customer_relationship:customer_relationship_id(
                      id, 
                      child:child_id(name)
                    ),
                    items:subscription_items(
                      id,
                      quantity,
                      unit_price,
                      subscription_plan(
                        name,
                        billing_period_type
                      )
                    )
                  `)
                  .eq('id', runData.subscription_id)
                  .single();
                
                if (subError) {
                  console.warn("Error loading detailed subscription:", subError);
                  // Use the basic data as fallback
                  subscriptionData = basicSubData;
                  setSubscription(basicSubData);
                } else {
                  subscriptionData = subData;
                  setSubscription(subData);
                  console.log("Loaded full subscription data:", subData);
                }
              }
            } catch (error) {
              console.error("Exception in subscription fetch:", error);
            }
          } else {
            console.log("No subscription ID associated with this playbook run");
          }

          // 3. Fetch task groups and tasks for this playbook run
          // First, debug the structure of the table to see what columns are available
          console.log("Debugging table structure...");
          try {
            const { data: sampleRow, error: sampleError } = await supabase
              .from('playbook_tasks')
              .select('*')
              .limit(1);
            
            if (sampleError) {
              console.error("Error fetching sample row:", sampleError);
            } else if (sampleRow && sampleRow.length > 0) {
              console.log("Available columns in playbook_tasks:", Object.keys(sampleRow[0]));
            } else {
              console.log("No sample row available");
            }
          } catch (e) {
            console.error("Error in debug query:", e);
          }

          // Now fetch all tasks for the run with the columns we want
          console.log("Fetching tasks for playbook run...");
          const { data: allTasksData, error: tasksError } = await supabase
            .from('playbook_tasks')
            .select(`
              id, 
              title, 
              description, 
              task_type, 
              status, 
              executed_at, 
              task_order, 
              playbook_template_group_id, 
              playbook_run_id,
              assigned_to,
              comments_count,
              due_date,
              assignees
            `)
            .eq('playbook_run_id', id);

          if (tasksError) {
            console.error('Error loading tasks:', tasksError);
            throw tasksError;
          }

          console.log("Retrieved tasks:", allTasksData?.length || 0, "tasks");
          
          // Get the distinct template group IDs from the tasks, filtering out any undefined/null values
          const templateGroupIds = [...new Set(allTasksData
            .map(task => task.playbook_template_group_id)
            .filter(id => id !== undefined && id !== null)
          )];
          console.log("Template group IDs:", templateGroupIds);

          // Fetch the details of these template groups
          console.log("Fetching template group details...");
          let templateGroupDetails = [];
          if (templateGroupIds.length > 0) {
            const { data, error: groupDetailsError } = await supabase
              .from('playbook_template_task_groups')
              .select('id, title, icon')
              .in('id', templateGroupIds);

            if (groupDetailsError) {
              console.error('Error loading template group details:', groupDetailsError);
            } else {
              templateGroupDetails = data || [];
              console.log("Retrieved template groups:", templateGroupDetails.length, "groups");
            }
          } else {
            console.log("No template group IDs found, skipping group fetch");
          }

          // Manually construct the taskGroups array by matching tasks to their template group details
          const constructedTaskGroups = [];

          // First add groups that have templates
          if (templateGroupDetails.length > 0) {
            templateGroupDetails.forEach(groupDetail => {
              const tasksForGroup = allTasksData
                .filter(task => task.playbook_template_group_id === groupDetail.id)
                .sort((a, b) => (a.task_order || 0) - (b.task_order || 0));
              
              if (tasksForGroup.length > 0) {
                constructedTaskGroups.push({
                  id: groupDetail.id,
                  title: groupDetail.title || 'Untitled Group',
                  icon: groupDetail.icon || '📋',
                  tasks: tasksForGroup
                });
              }
            });
          }

          // Find any tasks without a matching group and add them to an "Ungrouped" group
          const tasksWithoutGroup = allTasksData.filter(task => {
            // Either no template group ID or no matching template group found
            return !task.playbook_template_group_id || 
                   !templateGroupDetails.some(group => group.id === task.playbook_template_group_id);
          });

          if (tasksWithoutGroup.length > 0) {
            console.log(`Adding ${tasksWithoutGroup.length} ungrouped tasks to a default group`);
            constructedTaskGroups.push({
              id: 'ungrouped',
              title: 'Tarefas não agrupadas',
              icon: '📋',
              tasks: tasksWithoutGroup.sort((a, b) => (a.task_order || 0) - (b.task_order || 0))
            });
          }

          // If we still have no groups but have tasks, put all tasks in a default group
          if (constructedTaskGroups.length === 0 && allTasksData.length > 0) {
            console.log(`No groups created but have ${allTasksData.length} tasks. Creating default group.`);
            constructedTaskGroups.push({
              id: 'default',
              title: 'Tarefas',
              icon: '📋',
              tasks: allTasksData.sort((a, b) => (a.task_order || 0) - (b.task_order || 0))
            });
          }

          console.log("Constructed task groups:", constructedTaskGroups);
          setTaskGroups(constructedTaskGroups);

          // 4. Fetch users data for assignees
          console.log("Fetching users data...");
          try {
            // Fetch all columns first to allow for dynamic property checking
            const { data: usersData, error: usersError } = await supabase
              .from('users')
              .select('*');

            if (usersError) {
              console.error('Error loading users:', usersError);
            } else if (usersData) {
              console.log("Retrieved users:", usersData.length);
              // Create a map of user data by id for easy lookup
              const usersMap: Record<string, any> = {};
              
              // Process each user and fetch their avatar
              await Promise.all(usersData.map(async (user: any) => {
                // Check for required id property
                if (!user || !user.id) {
                  console.warn("Skipping user record without ID:", user);
                  return; 
                }
                
                // Safely access properties
                const firstName = user.first_name || '';
                const lastName = user.last_name || '';
                const email = user.email || '';
                const avatarFileName = user.profile_image_file_url; // Filename from database
                
                // Generate fallback avatar URL
                const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(firstName + '+' + lastName)}&background=f0f3fa&color=4763e4&bold=true`;
                
                // Try to fetch the real avatar if available
                let avatarUrl = fallbackAvatar;
                if (avatarFileName) {
                  // Try to fetch and create a blob URL from storage
                  const fetchedUrl = await fetchAvatarImage(avatarFileName);
                  if (fetchedUrl) {
                    avatarUrl = fetchedUrl;
                  }
                }

                usersMap[user.id] = {
                  id: user.id,
                  fullName: `${firstName} ${lastName}`.trim() || 'Unknown User',
                  initials: `${firstName.charAt(0) || ''}${lastName.charAt(0) || ''}`.toUpperCase().trim() || 'UN',
                  email: email,
                  avatar: avatarUrl,
                  avatarFileName: avatarFileName // Store filename for later reference
                };
              }));
              
              setUsers(usersMap);
            } else {
              console.log("No user data found");
            }
          } catch (error) {
            console.error('Error in user data fetching:', error);
          }

          // 5. Set the playbook run state with all the data we have
          console.log("Setting playbook run state with all data");
          setPlaybookRun({
            ...runData,
            name: runData.name || runData.template?.name || 'Untitled Playbook Run',
            description: runData.description || runData.template?.description || 'No description available',
            subscription: subscriptionData
          });

          console.log("Playbook run loaded successfully");
        } catch (error: any) {
          console.error('Error fetching playbook run data:', error);
          throw error; // Re-throw to be caught by the outer try/catch
        }

        // Continue with the rest of the function...
      } catch (error: any) {
        console.error('Error loading data:', error);
    toast({
          title: "Erro ao carregar playbook",
          description: `Não foi possível carregar os dados do playbook. ${error?.message || ''}`,
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadPlaybookRun();
  }, [id, toast]);
  
  if (isLoading) {
    return (
      <div className="w-full px-6 py-6">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full"></div>
        </div>
      </div>
    );
  }

  if (!playbookRun) {
    return (
      <div className="w-full px-6 py-6">
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Playbook run not found</p>
        </div>
      </div>
    );
  }
  
  // Calculate task statistics
  let completedTasks = 0;
  let totalTasks = 0;
  let nextTaskTitle = '';
  
  taskGroups.forEach(group => {
    if (group.tasks && Array.isArray(group.tasks)) {
      totalTasks += group.tasks.length;
      completedTasks += group.tasks.filter((task: any) => task.status === 'completed').length;
      
      // Find the first non-completed task to use as the "next task"
      if (!nextTaskTitle) {
        const nextTask = group.tasks.find((task: any) => task.status !== 'completed');
        if (nextTask) {
          nextTaskTitle = nextTask.title;
        }
      }
    }
  });
  
  const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  
  const handleInviteParticipants = () => {
    console.log('Invite participants clicked');
    // In a real implementation, this would open a modal or navigate to an invite page
  }
  
  const handleShare = () => {
    setShowShareDialog(true);
    // Generate a shareable link with the real ID
    setPublicLink(`${window.location.origin}/p/${playbookRun.id}/${Date.now()}`);
  }
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicLink);
    toast({
      title: "Link copied",
      description: "The shareable link has been copied to your clipboard.",
    });
  }

  const handleTaskClick = (task: any) => {
    // Se a tarefa já estiver concluída, apenas mostra os detalhes
    if (task.status === 'completed') {
      setSelectedTask(task);
      setShowTaskDetailDialog(true);
      return;
    }
    
    // Se não estiver concluída, marca como concluída automaticamente
    handleStatusChange('completed', task);
    
    // Feedback visual imediato para o usuário
    toast({
      title: "Tarefa concluída",
      description: "A tarefa foi marcada como concluída com sucesso."
    });
  }

  const handleTaskDetails = (e: React.MouseEvent, task: any) => {
    e.stopPropagation(); // Impedir que o evento de clique chegue ao handleTaskClick
    setSelectedTask(task);
    setShowTaskDetailDialog(true);
  }

  const handleSendEmail = () => {
    setShowEmailTemplateDialog(true);
  }

  const handleEditTask = () => {
    setShowEditTaskDialog(true);
  }

  const handleStatusChange = async (newStatus: string, taskToUpdate = selectedTask) => {
    if (!taskToUpdate) return;
    
    try {
      // Update task status in the database
      const { error } = await supabase
        .from('playbook_tasks')
        .update({ 
          status: newStatus,
          executed_at: newStatus === 'completed' ? new Date().toISOString() : null
        })
        .eq('id', taskToUpdate.id);
        
      if (error) throw error;
      
      // Update task in the local state
      const updatedTaskGroups = taskGroups.map(group => ({
        ...group,
        tasks: group.tasks.map((task: any) => 
          task.id === taskToUpdate.id 
            ? { 
                ...task, 
                status: newStatus, 
                executed_at: newStatus === 'completed' ? new Date().toISOString() : null 
              } 
            : task
        )
      }));
      
      setTaskGroups(updatedTaskGroups);
      
      // Atualizar o selectedTask apenas se for a tarefa selecionada atualmente
      if (selectedTask && selectedTask.id === taskToUpdate.id) {
        setSelectedTask({
          ...selectedTask,
          status: newStatus,
          executed_at: newStatus === 'completed' ? new Date().toISOString() : null
        });
      }
      
      // Só exibe o toast quando a tarefa é alterada a partir do modal, não do clique direto
      if (selectedTask) {
        toast({
          title: "Tarefa atualizada",
          description: `Status da tarefa alterado para ${newStatus === 'completed' ? 'concluído' : newStatus === 'pending' ? 'pendente' : 'ignorado'}.`,
        });
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      toast({
        title: "Erro ao atualizar tarefa",
        description: "Falha ao atualizar o status da tarefa.",
        variant: "destructive"
      });
    }
  }

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  }

  const getUserData = (userId: string | null | undefined) => {
    if (!userId) {
      console.log("No user ID provided to getUserData");
      return {
        id: "unknown",
        fullName: "Não atribuído",
        initials: "UN",
        email: "",
        avatar: `https://ui-avatars.com/api/?name=UN&background=f0f0f0&color=888888&bold=true`,
        avatarFileName: null
      };
    }

    if (users[userId]) {
      // Extract user details
      const user = users[userId];
      const initials = user.initials || "UN";
      const fullName = user.fullName || "Usuário não encontrado";
      const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=f0f3fa&color=4763e4&bold=true`;
      
      // If we have a real avatar use it, otherwise use fallback
      const avatarUrl = user.avatar || fallbackAvatar;
      
      return {
        ...user,
        avatar: avatarUrl,
        // Always ensure these fallbacks are available
        initials: initials,
        fullName: fullName
      };
    }

    console.log(`User with ID ${userId} not found in users map`);
    // Fallback for when user is not found
    return {
      id: userId,
      fullName: 'Usuário não encontrado',
      initials: 'UN',
      email: "",
      avatar: `https://ui-avatars.com/api/?name=UN&background=f0f0f0&color=888888&bold=true`,
      avatarFileName: null
    };
  };

  const getAssigneeId = (task: any) => {
    try {
      // First try assigned_to
      if (task?.assigned_to) {
        return task.assigned_to;
      }
      
      // Then try assignees as array
      if (task?.assignees) {
        // Handle array format
        if (Array.isArray(task.assignees) && task.assignees.length > 0) {
          return task.assignees[0];
        }
        
        // Handle JSON string that needs to be parsed
        if (typeof task.assignees === 'string') {
          try {
            // Try to parse as JSON if it looks like a JSON array
            if (task.assignees.startsWith('[') && task.assignees.endsWith(']')) {
              const parsed = JSON.parse(task.assignees);
              if (Array.isArray(parsed) && parsed.length > 0) {
                return parsed[0];
              }
            }
            
            // Check if it looks like a UUID
            if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(task.assignees)) {
              return task.assignees;
            }
          } catch (e) {
            console.log("Error parsing assignees JSON:", e);
            // If parsing fails, just use as is if it looks like a valid string
            if (task.assignees.length > 0 && !task.assignees.includes(' ')) {
              return task.assignees;
            }
          }
        }
      }
      
      console.log("No valid assignee found for task:", task?.id);
      // Return null if no valid assignee found
      return null;
    } catch (error) {
      console.error("Error in getAssigneeId:", error, "for task:", task);
      return null;
    }
  };

  const filteredTaskGroups = taskGroups.map(group => {
    if (!group) return null;
    
    // Handle all groups the same way now
    return {
      ...group,
      tasks: (group.tasks || [])
        .filter((task) => {
          if (!task) return false;
          
          const taskTitle = (task.title || '').toLowerCase();
          const taskDesc = (task.description || '').toLowerCase();
          const searchTermLower = searchTerm.toLowerCase();
          
          return taskTitle.includes(searchTermLower) || taskDesc.includes(searchTermLower);
        })
        // Ensure tasks are sorted by task_order
        .sort((a, b) => (a.task_order || 0) - (b.task_order || 0))
    };
  }).filter(group => group && (group.tasks || []).length > 0);
  
  return (
    <div className="w-full px-6 py-6">
      {/* Enhanced Header with title, buttons and metadata */}
      <div className="mb-8">
        {/* Back button and breadcrumbs */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
          <Button variant="ghost" onClick={() => window.history.back()} className="p-0 h-auto">
            <ArrowLeft className="h-4 w-4 mr-1" />
          </Button>
          <Link to="/playbooks/runs" className="hover:underline">Execuções</Link>
          <span>/</span>
          <span className="truncate max-w-[200px]">{playbookRun.name}</span>
        </div>
        
        {/* Main header with title, description and action buttons */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          <div className="flex-1">
            <h1 className="text-3xl font-extrabold flex items-center gap-2">
              {playbookRun.name}
              {progress === 100 && (
                <Badge variant="secondary" className="bg-green-100 text-green-700 hover:bg-green-100">
                  <CheckCircle className="h-3 w-3 mr-1" /> Concluído
                </Badge>
              )}
            </h1>
            <p className="text-muted-foreground mt-1">{playbookRun.description}</p>
            
            {/* Client and metadata info */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3">
        <div className="flex items-center gap-2">
                <Building className="h-4 w-4 text-muted-foreground" />
                <span className="font-extrabold">{playbookRun.subscription?.customer_relationship?.child?.name || "John Weiss"}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>{formatDate(playbookRun.started_at) || "29/03/2025"}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                <span>{completedTasks} de {totalTasks} concluídas</span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 items-center md:justify-end">
            <div className="w-full md:w-auto">
              <div className="bg-gray-100 rounded-full h-2 w-full md:w-32">
                <div 
                  className="bg-primary h-full rounded-full" 
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="text-xs mt-1 text-center">{progress}% completo</div>
            </div>
            
            <div className="flex gap-2 mt-2 md:mt-0">
              <Button variant="outline" size="sm" onClick={handleShare}>
                <Share2 className="h-4 w-4 mr-2" />
            Compartilhar
          </Button>
              <Button variant="default" size="sm">
                <Play className="h-4 w-4 mr-2" />
                Continuar
              </Button>
              <div className="relative">
                <Button variant="ghost" size="sm">
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.625 7.5C3.625 8.12132 3.12132 8.625 2.5 8.625C1.87868 8.625 1.375 8.12132 1.375 7.5C1.375 6.87868 1.87868 6.375 2.5 6.375C3.12132 6.375 3.625 6.87868 3.625 7.5ZM8.625 7.5C8.625 8.12132 8.12132 8.625 7.5 8.625C6.87868 8.625 6.375 8.12132 6.375 7.5C6.375 6.87868 6.87868 6.375 7.5 6.375C8.12132 6.375 8.625 6.87868 8.625 7.5ZM13.625 7.5C13.625 8.12132 13.1213 8.625 12.5 8.625C11.8787 8.625 11.375 8.12132 11.375 7.5C11.375 6.87868 11.8787 6.375 12.5 6.375C13.1213 6.375 13.625 6.87868 13.625 7.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                  </svg>
          </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Execution Details Section - New Design with Tabs */}
      <Card className="mb-6 overflow-hidden border-none shadow-md max-w-5xl mx-auto">
        <Tabs defaultValue="overview" className="w-full">
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 border-b">
            <div className="flex justify-between items-center">
              <TabsList className="bg-white/80 backdrop-blur-sm">
                <TabsTrigger value="overview">Visão Geral</TabsTrigger>
                <TabsTrigger value="team">Equipe</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
                <TabsTrigger value="metrics">Métricas</TabsTrigger>
              </TabsList>
              
              <Button variant="outline" size="sm" className="bg-white/80 backdrop-blur-sm">
                <FileText className="h-3.5 w-3.5 mr-1" /> Exportar
              </Button>
            </div>
          </div>
                  
          {/* Overview Tab */}
          <TabsContent value="overview" className="m-0">
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Left column with basic info */}
              <div className="space-y-5">
                <div className="flex items-start space-x-4">
                  <div className="bg-primary/10 p-3 rounded-xl">
                    <Building className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-extrabold text-muted-foreground">Cliente</h3>
                    <p className="font-extrabold text-base">
                      {playbookRun.subscription?.customer_relationship?.child?.name || "John Weiss"}
                    </p>
                    <div className="flex items-center mt-1 text-xs text-muted-foreground">
                      <FileText className="h-3 w-3 mr-1 inline" />
                      <span>ID: {playbookRun.subscription?.id?.substring(0, 8) || "4310a84f"}</span>
                    </div>
              </div>
            </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-amber-100 p-3 rounded-xl">
                    <Calendar className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-extrabold text-muted-foreground">Período</h3>
                    <p className="font-extrabold text-base">{formatDate(playbookRun.started_at) || "29/03/2025"}</p>
                    <div className="flex items-center mt-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3 mr-1 inline" />
                      <span>30 dias (restam 14)</span>
                    </div>
              </div>
            </div>
                  
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-xl">
                    <AlertCircle className="h-5 w-5 text-green-600" />
                  </div>
              <div>
                    <h3 className="text-sm font-extrabold text-muted-foreground">Status</h3>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className={`${
                        playbookRun.subscription?.state === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : playbookRun.subscription?.state === 'pending'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-purple-100 text-purple-700'
                      }`}>
                        {playbookRun.subscription?.state || "Em andamento"}
                      </Badge>
              </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Última atualização: Hoje às 14:32
            </div>
          </div>
                </div>
              </div>
              
              {/* Center column with progress */}
              <div className="flex flex-col items-center justify-center">
                <div className="relative inline-flex items-center justify-center mb-3">
                  {/* SVG progress circle */}
                  <svg className="w-36 h-36 transform -rotate-90">
                    <circle 
                      cx="72" 
                      cy="72" 
                      r="64" 
                      stroke="#e5e7eb" 
                      strokeWidth="12" 
                      fill="none" 
                    />
                    <circle 
                      cx="72" 
                      cy="72" 
                      r="64" 
                      stroke="url(#gradient)" 
                      strokeWidth="12" 
                      fill="none" 
                      strokeDasharray={402} 
                      strokeDashoffset={402 - (402 * progress / 100)} 
                      strokeLinecap="round"
                    />
                    
                    {/* Gradient definition */}
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#4763e4" />
                        <stop offset="100%" stopColor="#6366f1" />
                      </linearGradient>
                    </defs>
                  </svg>
                  
                  {/* Text inside circle */}
                  <div className="absolute flex flex-col items-center justify-center">
                    <span className="text-3xl font-extrabold">{progress}%</span>
                    <span className="text-xs text-muted-foreground">Concluído</span>
          </div>
                </div>
                
                <div className="text-center space-y-1">
                  <p className="text-sm font-extrabold">
                    {completedTasks} de {totalTasks} tarefas concluídas
                  </p>
                  <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    <span className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-green-500 mr-1"></div>
                      Concluídas: {completedTasks}
                    </span>
                    <span className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-yellow-500 mr-1"></div>
                      Pendentes: {totalTasks - completedTasks}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Right column with next actions */}
              <div className="space-y-4">
                <div className="bg-white rounded-xl border p-4 shadow-sm">
                  <h3 className="text-sm font-extrabold mb-2">Próxima ação</h3>
                  <p className="text-sm font-extrabold mb-2">
                    {nextTaskTitle || "Tell us more about your account setup"}
                  </p>
                  <div className="flex justify-between items-start">
                    <div className="flex items-center text-xs text-muted-foreground">
                      <Clock className="h-3 w-3 mr-1" />
                      <span>Prazo: 3 dias</span>
                    </div>
                    <Button variant="default" size="sm" className="h-7 text-xs">
                      <Play className="h-3 w-3 mr-1" /> 
                      Continuar
                </Button>
              </div>
                </div>
                
                <div className="bg-white rounded-xl border p-4 shadow-sm">
                  <h3 className="text-sm font-extrabold mb-2">Template utilizado</h3>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-extrabold">Onboarding Premium</p>
                      <div className="flex items-center text-xs text-muted-foreground mt-1">
                        <span>Versão 2.3</span>
                      </div>
                    </div>
                    <Badge variant="outline" className="h-5 text-[10px]">Premium</Badge>
            </div>
          </div>
          
                <div className="flex gap-2 mt-2">
                  <Button variant="outline" size="sm" className="flex-1 h-9 text-xs">
                    <Eye className="h-3 w-3 mr-1" />
                    Ver Relatório
            </Button>
                  <Button variant="outline" size="sm" className="flex-1 h-9 text-xs">
                    <MessageSquare className="h-3 w-3 mr-1" />
                    Comentários
            </Button>
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Team Tab */}
          <TabsContent value="team" className="m-0">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-extrabold">Equipe do Projeto</h3>
                <Button variant="outline" size="sm">
                  <Users className="h-4 w-4 mr-2" /> Adicionar Membro
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Team member card */}
                <div className="bg-white rounded-xl border p-4 flex items-center space-x-3">
                  <Avatar className="h-12 w-12 border-2 border-primary/10">
                    <AvatarImage src="https://ui-avatars.com/api/?name=JD&background=4763e4&color=fff" />
                    <AvatarFallback>JD</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-extrabold">Jane Doe</p>
                    <p className="text-sm text-muted-foreground">Customer Success</p>
                    <div className="flex items-center mt-1">
                      <Badge variant="outline" className="text-[10px] h-5 px-1">Líder</Badge>
                    </div>
                  </div>
                </div>
                
                {/* Team member card */}
                <div className="bg-white rounded-xl border p-4 flex items-center space-x-3">
                  <Avatar className="h-12 w-12 border-2 border-primary/10">
                    <AvatarImage src="https://ui-avatars.com/api/?name=JS&background=f59e0b&color=fff" />
                    <AvatarFallback>JS</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-extrabold">John Smith</p>
                    <p className="text-sm text-muted-foreground">Implementação</p>
                    <div className="flex items-center mt-1">
                      <Badge variant="outline" className="text-[10px] h-5 px-1">Técnico</Badge>
                    </div>
                  </div>
                </div>
                
                {/* Team member card */}
                <div className="bg-white rounded-xl border p-4 flex items-center space-x-3">
                  <Avatar className="h-12 w-12 border-2 border-primary/10">
                    <AvatarImage src="https://ui-avatars.com/api/?name=AM&background=10b981&color=fff" />
                    <AvatarFallback>AM</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-extrabold">Alice Miller</p>
                    <p className="text-sm text-muted-foreground">Gestão de Contas</p>
                    <div className="flex items-center mt-1">
                      <Badge variant="outline" className="text-[10px] h-5 px-1">Contato Principal</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Timeline Tab */}
          <TabsContent value="timeline" className="m-0">
            <div className="p-6">
              <h3 className="text-lg font-extrabold mb-4">Linha do Tempo</h3>
              
              <div className="relative border-l border-gray-200 ml-3 space-y-6 py-2">
                {/* Timeline item */}
          <div className="relative">
                  <div className="absolute -left-3 mt-1.5 h-6 w-6 rounded-full border-2 border-white bg-primary flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                  <div className="ml-6">
                    <div className="flex items-center">
                      <p className="font-extrabold">Playbook iniciado</p>
                      <span className="ml-2 text-xs text-muted-foreground">Hoje, 09:30</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Jane Doe iniciou o playbook</p>
                  </div>
          </div>
            
                {/* Timeline item */}
                <div className="relative">
                  <div className="absolute -left-3 mt-1.5 h-6 w-6 rounded-full border-2 border-white bg-amber-500 flex items-center justify-center">
                    <CheckSquare className="h-3 w-3 text-white" />
                  </div>
                  <div className="ml-6">
                    <div className="flex items-center">
                      <p className="font-extrabold">Grupo de tarefas concluído</p>
                      <span className="ml-2 text-xs text-muted-foreground">Hoje, 11:45</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Grupo "Configuração Inicial" foi concluído</p>
                  </div>
                </div>
                
                {/* Timeline item */}
                <div className="relative">
                  <div className="absolute -left-3 mt-1.5 h-6 w-6 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center">
                    <MessageSquare className="h-3 w-3 text-gray-600" />
                  </div>
                  <div className="ml-6">
                    <div className="flex items-center">
                      <p className="font-extrabold">Comentário adicionado</p>
                      <span className="ml-2 text-xs text-muted-foreground">Hoje, 14:32</span>
                    </div>
                    <p className="text-sm text-muted-foreground">John Smith adicionou um comentário à tarefa "Validar requisitos"</p>
                  </div>
                </div>
                
                {/* Timeline item */}
                <div className="relative">
                  <div className="absolute -left-3 mt-1.5 h-6 w-6 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center">
                    <Mail className="h-3 w-3 text-gray-600" />
                  </div>
                  <div className="ml-6">
                    <div className="flex items-center">
                      <p className="font-extrabold">Email enviado</p>
                      <span className="ml-2 text-xs text-muted-foreground">Amanhã (agendado)</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Email de follow-up será enviado para o cliente</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Metrics Tab */}
          <TabsContent value="metrics" className="m-0">
            <div className="p-6">
              <h3 className="text-lg font-extrabold mb-4">Métricas</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Metric card */}
                <div className="bg-white rounded-xl border p-4">
                  <div className="text-muted-foreground text-sm mb-1">Prazo médio de conclusão</div>
                  <div className="text-2xl font-extrabold">2.4 dias</div>
                  <div className="text-xs text-green-600 flex items-center mt-1">
                    <svg className="w-3 h-3 mr-1" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.5 6.5L5 9L9.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    14% mais rápido que o padrão
                  </div>
                </div>
                
                {/* Metric card */}
                <div className="bg-white rounded-xl border p-4">
                  <div className="text-muted-foreground text-sm mb-1">Taxa de conclusão</div>
                  <div className="text-2xl font-extrabold">{Math.round((completedTasks / totalTasks) * 100)}%</div>
                  <div className="text-xs text-amber-600 flex items-center mt-1">
                    <svg className="w-3 h-3 mr-1" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 2.5V9.5M6 9.5L9 6.5M6 9.5L3 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Em andamento
                  </div>
                </div>
                
                {/* Metric card */}
                <div className="bg-white rounded-xl border p-4">
                  <div className="text-muted-foreground text-sm mb-1">Satisfação do cliente</div>
                  <div className="text-2xl font-extrabold">---</div>
                  <div className="text-xs text-muted-foreground flex items-center mt-1">
                    <svg className="w-3 h-3 mr-1" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.5 6H2.5M2.5 6L5.5 9M2.5 6L5.5 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Aguardando feedback
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl border p-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-extrabold">Desempenho por etapa</h4>
                  <Select defaultValue="7days">
                    <SelectTrigger className="w-32 h-8 text-xs">
                      <SelectValue placeholder="Período" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7days">Últimos 7 dias</SelectItem>
                      <SelectItem value="30days">Últimos 30 dias</SelectItem>
                      <SelectItem value="all">Todo o período</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-4">
                  {/* Placeholder for chart */}
                  <div className="h-40 bg-gray-50 rounded flex items-center justify-center">
                    <p className="text-sm text-muted-foreground">Gráfico de desempenho seria exibido aqui</p>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <p className="text-muted-foreground">Etapa mais eficiente</p>
                      <p className="font-extrabold">Configuração</p>
                      <p className="text-xs text-green-600">1.2 dias (média)</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Etapa mais demorada</p>
                      <p className="font-extrabold">Validação</p>
                      <p className="text-xs text-amber-600">4.5 dias (média)</p>
                  </div>
                    <div>
                      <p className="text-muted-foreground">Tarefas atrasadas</p>
                      <p className="font-extrabold">0 tarefas</p>
                      <p className="text-xs text-green-600">Nenhum atraso</p>
                  </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
              </Card>
      
      {/* Tasks Section */}
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-extrabold">Tarefas do Playbook</h2>
            <p className="text-sm text-muted-foreground">Lista de tarefas e atividades a serem realizadas</p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <div className="relative w-60">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
              <Input
                placeholder="Buscar tarefas..." 
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Button variant="outline" size="sm" onClick={handleInviteParticipants}>
              <Users className="h-4 w-4 mr-2" />
              Convidar participantes
            </Button>
          </div>
        </div>

        {/* View options */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start">
          <div className="flex items-center">
            {/* List/Kanban view selector removed temporarily to fix error */}
            <span className="text-sm font-extrabold">Visualização em Lista</span>
                  </div>
          
          <div className="flex items-center">
            {/* Client/Internal view selector removed temporarily to fix error */}
            <span className="text-sm font-extrabold">Visão do cliente</span>
              </div>
          </div>
          
        <div className="text-sm text-muted-foreground mb-4">
          Apenas tarefas visíveis para participantes. Esta é a visão que seu cliente terá.
        </div>
        
        {/* Task Groups - List View (Fixed version without Tabs/TabsContent) */}
        <div className="grid grid-cols-1 gap-4">
          {filteredTaskGroups.length > 0 ? (
            filteredTaskGroups.map((group, groupIndex) => (
              <Card key={group?.id || `group-${groupIndex}`} className="overflow-hidden">
                {/* Group Header */}
                <div className="bg-gray-50 p-3 border-b">
                  <div className="flex items-center gap-2">
                    <div className="h-7 w-7 rounded-full bg-white flex items-center justify-center border shadow-sm">
                      {group?.icon || (groupIndex === 0 ? "🎯" : "🔧")}
                    </div>
                    <div>
                      <h3 className="text-sm font-extrabold">
                        {group?.title || (groupIndex === 0 ? "Getting started" : "Implementation")}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="text-xs text-muted-foreground">
                          {(group?.tasks || []).filter((t: any) => t.status === 'completed').length}/{(group?.tasks || []).length} tarefas concluídas
                        </div>
                        <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-500 rounded-full" 
                            style={{ 
                              width: `${(group?.tasks || []).length ? 
                                Math.round(((group?.tasks || []).filter((t: any) => t.status === 'completed').length / (group?.tasks || []).length) * 100) : 0}%` 
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                      <div className="flex h-5 items-center rounded-full bg-gray-100 px-2 text-xs font-extrabold text-gray-600">
                        {(group?.tasks || []).length || 0} tarefas
                      </div>
                      <div className="flex h-5 items-center rounded-full bg-orange-100 px-2 text-xs font-extrabold text-orange-600">
                        Fase {groupIndex + 1}
                      </div>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3.625 7.5C3.625 8.12132 3.12132 8.625 2.5 8.625C1.87868 8.625 1.375 8.12132 1.375 7.5C1.375 6.87868 1.87868 6.375 2.5 6.375C3.12132 6.375 3.625 6.87868 3.625 7.5ZM8.625 7.5C8.625 8.12132 8.12132 8.625 7.5 8.625C6.87868 8.625 6.375 8.12132 6.375 7.5C6.375 6.87868 6.87868 6.375 7.5 6.375C8.12132 6.375 8.625 6.87868 8.625 7.5ZM13.625 7.5C13.625 8.12132 13.1213 8.625 12.5 8.625C11.8787 8.625 11.375 8.12132 11.375 7.5C11.375 6.87868 11.8787 6.375 12.5 6.375C13.1213 6.375 13.625 6.87868 13.625 7.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                        </svg>
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Tasks */}
                <div className="divide-y">
                  {(group?.tasks || []).map((task: any, index: number) => {
                    if (!task) return null;
                    
                    const assigneeId = getAssigneeId(task);
                    const isPending = !task.status || task.status === 'pending';
                    const isCompleted = task.status === 'completed';
                    
                    return (
                      <div 
                        key={task.id || `task-${index}`}
                        className="p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleTaskClick(task)}
                      >
                        <div className="flex items-start gap-2">
                          {/* Status indicator */}
                          <div className="pt-0.5 min-w-[20px]">
                            {isCompleted ? (
                              <div className="h-5 w-5 rounded-full flex items-center justify-center bg-green-500 text-white">
                                <Check className="h-3 w-3" />
                              </div>
                            ) : task.status === 'delayed' || task.status === 'skipped' ? (
                              <div className="h-5 w-5 rounded-full flex items-center justify-center border-2 border-red-500 text-red-500">
                                <X className="h-3 w-3" />
                              </div>
                            ) : task.status === 'in_progress' ? (
                              <div className="h-5 w-5 rounded-full flex items-center justify-center border-2 border-blue-500 text-blue-500">
                                <Circle className="h-3 w-3 fill-current" />
                              </div>
                            ) : (
                              <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                            )}
                          </div>
                          
                          {/* Task content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap justify-between items-start gap-1">
                              <h4 className={`text-xs font-extrabold ${isCompleted ? 'line-through text-gray-400' : ''}`}>
                                {task.title}
                              </h4>
                              
                              {/* Status badges */}
                              {isCompleted && (
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 h-4 text-[10px] px-1">
                                  Concluída
                                </Badge>
                              )}
                              
                              {task.status === 'in_progress' && (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 h-4 text-[10px] px-1">
                                  Em andamento
                                </Badge>
                              )}
                              
                              {task.status === 'delayed' && (
                                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 h-4 text-[10px] px-1">
                                  Atrasada
                                </Badge>
                              )}
                              
                              {isPending && (
                                <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 h-4 text-[10px] px-1">
                                  Pendente
                                </Badge>
                              )}
                            </div>
                            
                            {/* Task description - only show for non-completed tasks */}
                            {task.description && !isCompleted && (
                              <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">
                                {task.description}
                              </p>
                            )}
                            
                            {/* Metadata row */}
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                              {/* Assignee */}
                              <div className="flex items-center gap-1">
                                {assigneeId ? (
                                  <>
                                    <Avatar className="h-4 w-4">
                                      <AvatarImage src={getUserData(assigneeId).avatar} alt={getUserData(assigneeId).initials} />
                                      <AvatarFallback className="text-[8px]">{getUserData(assigneeId).initials}</AvatarFallback>
                                    </Avatar>
                                    <span className="text-[10px] text-gray-600">{getUserData(assigneeId).fullName}</span>
                                  </>
                                ) : (
                                  <>
                                    <Avatar className="h-4 w-4">
                                      <AvatarFallback className="text-[8px] bg-gray-100 text-gray-600">UN</AvatarFallback>
                                    </Avatar>
                                    <span className="text-[10px] text-gray-500">Não atribuído</span>
                                  </>
                                )}
                              </div>
                              
                              {/* Due date */}
                              <div className="flex items-center gap-1">
                                <Clock className="h-2.5 w-2.5 text-gray-400" />
                                <span className="text-[10px] text-gray-600">
                                  {task.due_date ? formatDate(task.due_date) : "29/03"}</span>
                              </div>
                              
                              {/* Comments */}
                              {(task.comments_count > 0) && (
                                <div className="flex items-center gap-1">
                                  <MessageSquare className="h-2.5 w-2.5 text-gray-400" />
                                  <span className="text-[10px] text-gray-600">{task.comments_count} comentários</span>
                                </div>
                              )}
                              
                              {/* Task ID and Details button */}
                              <div className="ml-auto flex items-center gap-1">
                                <span className="text-[10px] text-gray-400">
                                  #{task.id?.substring(0, 8) || index + 1}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5"
                                  onClick={(e) => handleTaskDetails(e, task)}
                                  title="Ver detalhes"
                                >
                                  <Eye className="h-3 w-3 text-gray-500" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Add task button */}
                <div className="p-2 bg-gray-50 border-t">
                  <Button variant="ghost" size="sm" className="w-full h-7 justify-start text-xs text-gray-500 hover:text-gray-900">
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1">
                      <path d="M8 2.75C8 2.47386 7.77614 2.25 7.5 2.25C7.22386 2.25 7 2.47386 7 2.75V7H2.75C2.47386 7 2.25 7.22386 2.25 7.5C2.25 7.77614 2.47386 8 2.75 8H7V12.25C7 12.5261 7.22386 12.75 7.5 12.75C7.77614 12.75 8 12.5261 8 12.25V8H12.25C12.5261 8 12.75 7.77614 12.75 7.5C12.75 7.22386 12.5261 7 12.25 7H8V2.75Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                    </svg>
                    Adicionar tarefa
                </Button>
                </div>
              </Card>
            ))
          ) : (
            <Card className="p-6 text-center">
              <div className="mx-auto w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                <svg width="20" height="20" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1.5 9.5C1.5 7.01472 3.51472 5 6 5C8.48528 5 10.5 7.01472 10.5 9.5C10.5 11.9853 8.48528 14 6 14C3.51472 14 1.5 11.9853 1.5 9.5ZM6 6C4.067 6 2.5 7.567 2.5 9.5C2.5 11.433 4.067 13 6 13C7.933 13 9.5 11.433 9.5 9.5C9.5 7.567 7.933 6 6 6ZM7.30049 8.44725C7.69441 8.05333 8.32499 8.05333 8.7189 8.44725C9.11282 8.84117 9.11282 9.47174 8.7189 9.86566L7.00001 11.5845L5.28111 9.86566C4.8872 9.47174 4.8872 8.84117 5.28111 8.44725C5.67503 8.05333 6.30561 8.05333 6.69953 8.44725L7.00001 8.74773L7.30049 8.44725ZM13.5 2C13.5 1.17157 12.8284 0.5 12 0.5C11.1716 0.5 10.5 1.17157 10.5 2C10.5 2.82843 11.1716 3.5 12 3.5C12.8284 3.5 13.5 2.82843 13.5 2ZM12 1.5C11.7239 1.5 11.5 1.72386 11.5 2C11.5 2.27614 11.7239 2.5 12 2.5C12.2761 2.5 12.5 2.27614 12.5 2C12.5 1.72386 12.2761 1.5 12 1.5ZM13.5 7.10003C13.5 5.34318 12.0069 3.85001 10.25 3.85001C8.49314 3.85001 7 5.34318 7 7.10003C7 8.85687 8.49314 10.35 10.25 10.35C12.0069 10.35 13.5 8.85687 13.5 7.10003ZM10.25 4.85001C8.64331 4.85001 8 6.27232 8 7.10003C8 7.92773 8.64331 9.35001 10.25 9.35001C11.8567 9.35001 12.5 7.92773 12.5 7.10003C12.5 6.27232 11.8567 4.85001 10.25 4.85001Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                </svg>
              </div>
              <p className="text-muted-foreground text-sm mb-3">Nenhuma tarefa encontrada para os critérios de busca</p>
              <Button variant="outline" size="sm" onClick={() => setSearchTerm("")}>
                Limpar busca
              </Button>
            </Card>
          )}
        </div>
      </div>
                  
      {/* Dialogs */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Compartilhar Playbook</DialogTitle>
            <DialogDescription>
              Crie um link compartilhável para que interessados externos possam visualizar este Playbook.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="flex items-center space-x-2">
              <Input value={publicLink} readOnly />
              <Button size="sm" onClick={handleCopyLink}>
                  <Copy className="h-4 w-4" />
            </Button>
              </div>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <ExternalLink className="h-4 w-4" />
              <span>Qualquer pessoa com o link pode visualizar este Playbook.</span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowShareDialog(false)}>Concluído</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showTaskDetailDialog} onOpenChange={setShowTaskDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedTask?.title}</DialogTitle>
            <DialogDescription>
              {selectedTask?.description}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            {/* Mensagem explicativa sobre o comportamento */}
            {selectedTask?.status !== 'completed' && (
              <div className="bg-blue-50 text-blue-700 p-3 rounded-md text-sm flex items-start">
                <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                <p>Dica: Você pode clicar diretamente sobre a tarefa para marcá-la como concluída rapidamente.</p>
              </div>
            )}
            
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Data de Entrega</span>
              </div>
              <span className="text-sm">{selectedTask?.due_date ? formatDate(selectedTask.due_date) : 'Sem data definida'}</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Atribuído a</span>
              </div>
              <div className="flex items-center gap-2">
                {(() => {
                  const assigneeId = selectedTask ? getAssigneeId(selectedTask) : null;
                  if (assigneeId) {
                    return (
                      <div className="flex items-center gap-2">
                        <img 
                          src={getUserData(assigneeId).avatar}
                          alt={getUserData(assigneeId).initials}
                          className="h-6 w-6 rounded-full object-cover"
                          onError={(e) => {
                            console.log('Error loading avatar image in task detail');
                            // Hide the broken image and show fallback
                            (e.target as HTMLImageElement).style.display = 'none';
                            // The AvatarFallback will be shown automatically
                          }}
                        />
                        <span className="text-sm">{getUserData(assigneeId).fullName}</span>
                        
                        {/* Show additional assignees if present */}
                        {selectedTask?.assignees && Array.isArray(selectedTask.assignees) && selectedTask.assignees.length > 1 && (
                          <span className="text-sm text-gray-400">{` e mais ${selectedTask.assignees.length - 1}`}</span>
                        )}
                  </div>
                    );
                  } else {
                    return (
                      <>
                        <Avatar className="h-6 w-6">
                          <AvatarFallback>
                            UN
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm">Não atribuído</span>
                      </>
                    );
                  }
                })()}
                  </div>
                  </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Status</span>
                  </div>
              <RadioGroup className="flex" value={selectedTask?.status} onValueChange={handleStatusChange}>
                <div className="flex items-center space-x-1 mr-4">
                  <RadioGroupItem value="pending" id="pending" />
                  <Label htmlFor="pending" className="text-xs">Pendente</Label>
                </div>
                <div className="flex items-center space-x-1 mr-4">
                  <RadioGroupItem value="completed" id="completed" />
                  <Label htmlFor="completed" className="text-xs">Concluído</Label>
                </div>
                <div className="flex items-center space-x-1">
                  <RadioGroupItem value="skipped" id="skipped" />
                  <Label htmlFor="skipped" className="text-xs">Ignorado</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
          <DialogFooter className="gap-2">
            {selectedTask?.status === 'completed' && (
              <Button variant="outline" onClick={() => handleStatusChange('pending')}>
                <Circle className="h-4 w-4 mr-2" />
                Marcar como Pendente
              </Button>
            )}
            <Button variant="outline" onClick={handleSendEmail}>
              <Mail className="h-4 w-4 mr-2" />
              Enviar Email
            </Button>
            <Button variant="outline" onClick={handleEditTask}>
              <FileText className="h-4 w-4 mr-2" />
              Editar Tarefa
            </Button>
            <Button onClick={() => setShowTaskDetailDialog(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showEmailTemplateDialog} onOpenChange={setShowEmailTemplateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Selecionar Modelo de Email</DialogTitle>
            <DialogDescription>
              Escolha um modelo de email para usar nesta tarefa.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <RadioGroup value={selectedEmailTemplate || ""} onValueChange={setSelectedEmailTemplate}>
              <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                  <RadioGroupItem value="template1" id="template1" />
                  <Label htmlFor="template1">Boas-vindas Onboarding</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                  <RadioGroupItem value="template2" id="template2" />
                  <Label htmlFor="template2">Acompanhamento de Reunião</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                  <RadioGroupItem value="template3" id="template3" />
                  <Label htmlFor="template3">Revisão Trimestral</Label>
                </div>
                  </div>
                </RadioGroup>
              </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEmailTemplateDialog(false)}>Cancelar</Button>
            <Button 
              disabled={!selectedEmailTemplate}
              onClick={() => {
                toast({
                  title: "Modelo de Email Selecionado",
                  description: "Modelo de email foi anexado a esta tarefa.",
                });
                setShowEmailTemplateDialog(false);
              }}
            >
              <Send className="h-4 w-4 mr-2" />
              Usar Modelo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showEditTaskDialog} onOpenChange={setShowEditTaskDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Tarefa</DialogTitle>
            <DialogDescription>
              Atualizar detalhes desta tarefa.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="task-title">Título</Label>
              <Input id="task-title" defaultValue={selectedTask?.title} />
                </div>
            <div>
              <Label htmlFor="task-description">Descrição</Label>
              <Input id="task-description" defaultValue={selectedTask?.description} />
            </div>
            <div>
              <Label htmlFor="task-due-date">Data de Entrega</Label>
              <Input id="task-due-date" type="date" defaultValue={selectedTask?.due_date} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditTaskDialog(false)}>Cancelar</Button>
            <Button onClick={() => {
              toast({
                title: "Tarefa Atualizada",
                description: "A tarefa foi atualizada com sucesso.",
              });
              setShowEditTaskDialog(false);
            }}>Salvar Alterações</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}